import {
  Button,
  Divider,
  FormGroup,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { useContext } from "react";
import SimulatorContext from "../../../../context/SimulatorContext";
import styles from "../../../components.module.sass";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(251,171,53,0.8)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const RedTableRow = styled(TableRow)(() => ({
  backgroundColor: "#FF0000",
  "& td, & th": {
    color: "white",
    fontWeight: 700,
    border: 0,
  },
}));

export const PmmGuardianForm = () => {
  const {
    pmmGuardianValues,
    pmmGuardianResults,
    onUpdatePmmGuardianValues,
    onSimulatePmmGuardian,
  } = useContext(SimulatorContext);

  const handleChange = (item: string, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) onUpdatePmmGuardianValues(item, num);
  };

  const hasResults =
    pmmGuardianResults.screwQuantity > 0 ||
    pmmGuardianResults.hydrostaticPressure > 0;

  return (
    <>
      <div className={styles.simulator}>
        <FormGroup>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography mt={2} variant="h6">
                Input Data
              </Typography>
              <Divider />
            </Grid>

            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="Specific Gravity of Fluid"
                value={pmmGuardianValues.specificGravity}
                variant="outlined"
                className={styles.simulatorField}
                onChange={(e) => handleChange("specificGravity", e.target.value)}
              />
            </Grid>

            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="Water Constant (psi/ft/SG)"
                value={pmmGuardianValues.waterConstant}
                variant="outlined"
                className={styles.simulatorField}
                onChange={(e) => handleChange("waterConstant", e.target.value)}
              />
            </Grid>

            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="Intake Pressure (psi)"
                value={pmmGuardianValues.intakePressure}
                variant="outlined"
                className={styles.simulatorField}
                onChange={(e) =>
                  handleChange("intakePressure", e.target.value)
                }
              />
            </Grid>

            <Grid item lg={4} sm={6} xs={12}>
              <TextField
                type="number"
                label="PMM Depth (ft)"
                value={pmmGuardianValues.pmmDepth}
                variant="outlined"
                className={styles.simulatorField}
                onChange={(e) => handleChange("pmmDepth", e.target.value)}
              />
            </Grid>
          </Grid>
        </FormGroup>

        <Button
          variant="outlined"
          sx={{ mt: 5 }}
          onClick={onSimulatePmmGuardian}
        >
          Simulate
        </Button>
      </div>

      {hasResults && (
        <div className={styles.simulatorResults}>
          <FormGroup>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography mt={2} variant="h6">
                  PMM Guardian
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <TableContainer>
                  <Table aria-label="pmm guardian results">
                    <TableBody>
                      <StyledTableRow>
                        <StyledTableCell component="th" scope="row">
                          Hydrostatic Pressure
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {pmmGuardianResults.hydrostaticPressure}
                        </StyledTableCell>
                        <StyledTableCell align="right">psi</StyledTableCell>
                      </StyledTableRow>

                      <RedTableRow>
                        <StyledTableCell component="th" scope="row">
                          Quantity of Screws in the Tool
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {pmmGuardianResults.screwQuantity}
                        </StyledTableCell>
                        <StyledTableCell align="right"></StyledTableCell>
                      </RedTableRow>

                      <RedTableRow>
                        <StyledTableCell component="th" scope="row">
                          Set Point (PMM Guardian)
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {pmmGuardianResults.setPoint}
                        </StyledTableCell>
                        <StyledTableCell align="right">psi</StyledTableCell>
                      </RedTableRow>

                      <StyledTableRow>
                        <StyledTableCell component="th" scope="row">
                          Surface Pressure to Actuate (PIP = 0)
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {pmmGuardianResults.surfacePressureZeroPip}
                        </StyledTableCell>
                        <StyledTableCell align="right">psi</StyledTableCell>
                      </StyledTableRow>

                      <StyledTableRow>
                        <StyledTableCell component="th" scope="row">
                          Surface Pressure to Actuate (with PIP)
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {pmmGuardianResults.surfacePressureWithPip}
                        </StyledTableCell>
                        <StyledTableCell align="right">psi</StyledTableCell>
                      </StyledTableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mt: 1, textAlign: "left" }}>
                  <strong>Notes:</strong> If they had{" "}
                  {pmmGuardianValues.intakePressure} psi intake pressure, it
                  would take{" "}
                  <strong>
                    {pmmGuardianResults.surfacePressureZeroPip} psi
                  </strong>{" "}
                  at surface to pop it once the dart/ball is on seat. If the
                  PIP is higher than 0 psi it will take the surface pressure
                  plus intake pressure — in this case{" "}
                  <strong>
                    {pmmGuardianResults.surfacePressureWithPip} psi
                  </strong>
                  .
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, textAlign: "left" }}>
                  As you remove screws, take them out 180° apart so you don't
                  end up with remaining screws all on one side of the tool. If
                  you add screws back in after they've been removed, tighten
                  until snug then back off ¼ of a turn.
                </Typography>
              </Grid>
            </Grid>
          </FormGroup>
        </div>
      )}
    </>
  );
};
